import type { BinaryChannel, BinaryChannelObserver, BinaryChannelState } from './BinaryChannel';

export interface ReconnectingWebSocketChannelOptions {
  readonly connectTimeoutMs?: number;
  readonly reconnectDelayMs?: number;
  readonly reconnect?: boolean;
  /** Zero requires an empty send buffer; positive values bound queued bytes. */
  readonly maxBufferedBytes?: number;
}

export class ReconnectingWebSocketChannel implements BinaryChannel {
  readonly #url: string;
  readonly #connectTimeoutMs: number;
  readonly #reconnectDelayMs: number;
  readonly #reconnect: boolean;
  readonly #maxBufferedBytes: number;
  readonly #observers = new Set<BinaryChannelObserver>();
  #socket: WebSocket | null = null;
  #state: BinaryChannelState = 'idle';
  #disposed = false;
  #connectedOnce = false;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(url: string, options: ReconnectingWebSocketChannelOptions = {}) {
    this.#url = url;
    this.#connectTimeoutMs = options.connectTimeoutMs ?? 4_000;
    this.#reconnectDelayMs = options.reconnectDelayMs ?? 1_500;
    this.#reconnect = options.reconnect ?? true;
    this.#maxBufferedBytes = options.maxBufferedBytes ?? 0;
    if (!Number.isSafeInteger(this.#maxBufferedBytes) || this.#maxBufferedBytes < 0) {
      throw new Error('WebSocket send buffer limit must be a non-negative integer.');
    }
  }

  get state(): BinaryChannelState {
    return this.#state;
  }

  async connect(): Promise<void> {
    if (this.#disposed) throw new Error('Binary channel is closed.');
    if (this.#socket?.readyState === WebSocket.OPEN) return;
    const socket = this.#socket ?? this.#openSocket();
    if (socket.readyState === WebSocket.OPEN) return;

    await new Promise<void>((resolve, reject) => {
      const timeout = globalThis.setTimeout(() => {
        cleanup();
        socket.close();
        reject(new Error(`WebSocket connection timed out: ${this.#url}`));
      }, this.#connectTimeoutMs);
      const cleanup = (): void => {
        globalThis.clearTimeout(timeout);
        socket.removeEventListener('open', handleOpen);
        socket.removeEventListener('close', handleClose);
      };
      const handleOpen = (): void => {
        cleanup();
        resolve();
      };
      const handleClose = (): void => {
        cleanup();
        reject(new Error(`WebSocket endpoint is unavailable: ${this.#url}`));
      };
      socket.addEventListener('open', handleOpen, { once: true });
      socket.addEventListener('close', handleClose, { once: true });
    });
  }

  send(payload: ArrayBuffer): boolean {
    const socket = this.#socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    if (this.#maxBufferedBytes === 0 ? socket.bufferedAmount !== 0
      : socket.bufferedAmount + payload.byteLength > this.#maxBufferedBytes) return false;
    socket.send(payload);
    return true;
  }

  subscribe(observer: BinaryChannelObserver): () => void {
    this.#observers.add(observer);
    return () => this.#observers.delete(observer);
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#state = 'closed';
    if (this.#reconnectTimer !== null) globalThis.clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = null;
    this.#socket?.close(1000, 'channel closed');
    this.#socket = null;
    this.#observers.clear();
  }

  #openSocket(): WebSocket {
    if (this.#disposed) throw new Error('Binary channel is closed.');
    this.#state = 'connecting';
    const socket = new WebSocket(this.#url);
    socket.binaryType = 'arraybuffer';
    socket.addEventListener('open', () => {
      if (this.#socket !== socket || this.#disposed) return;
      this.#state = 'open';
      this.#connectedOnce = true;
      for (const observer of this.#observers) observer.onOpen?.();
    });
    socket.addEventListener('message', (event) => {
      if (this.#socket !== socket || !(event.data instanceof ArrayBuffer)) return;
      for (const observer of this.#observers) observer.onMessage?.(event.data);
    });
    socket.addEventListener('close', () => {
      if (this.#socket !== socket) return;
      this.#socket = null;
      this.#state = this.#disposed ? 'closed' : 'idle';
      for (const observer of this.#observers) observer.onClose?.();
      if (!this.#disposed && this.#connectedOnce && this.#reconnect) this.#scheduleReconnect();
    });
    socket.addEventListener('error', () => socket.close());
    this.#socket = socket;
    return socket;
  }

  #scheduleReconnect(): void {
    if (this.#reconnectTimer !== null) globalThis.clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = globalThis.setTimeout(() => {
      this.#reconnectTimer = null;
      if (!this.#disposed && !this.#socket) this.#openSocket();
    }, this.#reconnectDelayMs);
  }
}
